import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
} from "@mui/material";
import {
  Draw,
  CloudUpload,
  Security,
  Speed,
  Devices,
  Verified,
  History,
  Share,
} from "@mui/icons-material";
import Layout from "../components/layout/LandingPageLayout";
import { useTheme } from "@mui/material/styles";
import { Link } from "react-router-dom";

const EsignInfo = () => {
  const theme = useTheme();

  const features = [
    {
      icon: <Draw sx={{ fontSize: 40 }} />,
      title: "Digital Signature",
      subtitle: "Sign Anywhere",
      description:
        "Create legally binding digital signatures with just a few clicks from any device.",
    },
    {
      icon: <CloudUpload sx={{ fontSize: 40 }} />,
      title: "Upload Documents",
      subtitle: "Easy Upload",
      description:
        "Upload PDFs, Word documents, and more. Support for multiple file formats.",
    },
    {
      icon: <Security sx={{ fontSize: 40 }} />,
      title: "Bank-Grade Security",
      subtitle: "256-bit Encryption",
      description:
        "Your documents are protected with enterprise-level encryption and security.",
    },
    {
      icon: <Speed sx={{ fontSize: 40 }} />,
      title: "Instant Signing",
      subtitle: "Real-Time",
      description:
        "Sign and send documents in seconds. No printing, scanning, or mailing required.",
    },
    {
      icon: <Devices sx={{ fontSize: 40 }} />,
      title: "Multi-Device",
      subtitle: "Sign On-the-Go",
      description:
        "Access and sign documents from desktop, tablet, or mobile devices seamlessly.",
    },
    {
      icon: <Verified sx={{ fontSize: 40 }} />,
      title: "Legal Compliance",
      subtitle: "Legally Valid",
      description:
        "Compliant with IT Act 2000 and other digital signature regulations in India.",
    },
    {
      icon: <History sx={{ fontSize: 40 }} />,
      title: "Audit Trail",
      subtitle: "Complete History",
      description:
        "Track every action with detailed audit logs and certificate of completion.",
    },
    {
      icon: <Share sx={{ fontSize: 40 }} />,
      title: "Share & Collaborate",
      subtitle: "Multiple Signers",
      description:
        "Send documents to multiple signers and track signing status in real-time.",
    },
  ];

  const benefits = [
    { number: "10x", label: "Faster Processing" },
    { number: "100%", label: "Legally Valid" },
    { number: "24/7", label: "Available" },
    { number: "Zero", label: "Paper Waste" },
  ];

  return (
    <Layout>
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.primary.main}03 100%)`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: -100,
            right: -100,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${theme.palette.primary.main}15 0%, transparent 70%)`,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -150,
            left: -150,
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${theme.palette.primary.main}10 0%, transparent 70%)`,
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          {/* Header Section */}
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Chip
              label="E-SIGNATURE"
              sx={{
                mb: 2,
                bgcolor: theme.palette.primary.main,
                color: "white",
                fontWeight: 600,
                letterSpacing: 1.2,
                px: 2,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Sign Documents Digitally
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "text.secondary",
                maxWidth: 700,
                mx: "auto",
                lineHeight: 1.7,
                fontSize: { xs: "1rem", md: "1.1rem" },
              }}
            >
              Say goodbye to paper. Sign, send, and manage documents securely
              with our legally compliant e-signature solution.
            </Typography>
          </Box>

          {/* Stats Section */}
          <Grid container spacing={3} sx={{ mb: 8 }}>
            {benefits.map((benefit, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box
                  sx={{
                    textAlign: "center",
                    p: 3,
                    borderRadius: 2,
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      transform: "translateY(-4px)",
                      boxShadow: `0 8px 16px ${theme.palette.primary.main}15`,
                    },
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      color: theme.palette.primary.main,
                      mb: 0.5,
                    }}
                  >
                    {benefit.number}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontSize: "0.75rem",
                    }}
                  >
                    {benefit.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Features Grid */}
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer",
                    border: "1px solid",
                    borderColor: "divider",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 12px 24px ${theme.palette.primary.main}20`,
                      borderColor: theme.palette.primary.main,
                      "& .feature-icon": {
                        transform: "scale(1.1) rotate(5deg)",
                        color: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      height: "100%",
                    }}
                  >
                    <Box
                      className="feature-icon"
                      sx={{
                        color: theme.palette.primary.main,
                        mb: 2,
                        transition: "all 0.3s ease",
                        opacity: 0.9,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        color: "text.primary",
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        mb: 1.5,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      {feature.subtitle}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.6,
                        fontSize: "0.875rem",
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* How It Works Section */}
          <Box sx={{ mt: 10, mb: 6 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                textAlign: "center",
                mb: 5,
                color: "text.primary",
              }}
            >
              How It Works
            </Typography>
            <Grid container spacing={4}>
              {[
                {
                  step: "01",
                  title: "Upload",
                  desc: "Upload your document or create one from scratch",
                },
                {
                  step: "02",
                  title: "Sign",
                  desc: "Add your signature, initials, or other required fields",
                },
                {
                  step: "03",
                  title: "Send",
                  desc: "Send to others for signature or download instantly",
                },
              ].map((item, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Box
                    sx={{
                      textAlign: "center",
                      position: "relative",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "4rem",
                        fontWeight: 900,
                        color: theme.palette.primary.main,
                        opacity: 0.2,
                        lineHeight: 1,
                      }}
                    >
                      {item.step}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                        color: "text.primary",
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.6,
                      }}
                    >
                      {item.desc}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Bottom CTA Section */}
          <Box
            sx={{
              mt: 8,
              p: 4,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.1,
                background:
                  "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.05) 10px, rgba(255,255,255,0.05) 20px)",
              }}
            />
            <Typography
              variant="h5"
              sx={{
                color: "white",
                fontWeight: 700,
                mb: 1,
                position: "relative",
                zIndex: 1,
              }}
            >
              Ready to go paperless?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "rgba(255,255,255,0.9)",
                position: "relative",
                zIndex: 1,
              }}
            >
              Join thousands of businesses signing documents digitally with
              confidence
              <Box>
                <Button
                  component={Link}
                  to="/login"
                  variant="contained"
                  size="large"
                  sx={{
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.primary.main,
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    "&:hover": {
                      bgcolor: theme.palette.grey[100],
                    },
                  }}
                >
                  Get Started Free
                </Button>
              </Box>
            </Typography>
          </Box>
        </Container>
      </Box>
    </Layout>
  );
};

export default EsignInfo;
